import React from 'react';
import { getConfidenceColor, getConfidenceLabel, getPriorityLabel, type VerificationQueueRow } from '../../services/rpc/verification';

interface VerificationQueueProps {
  programs: VerificationQueueRow[];
  isLoading: boolean;
  error: Error | null;
  onSelectProgram: (program: VerificationQueueRow) => void;
}

export const VerificationQueue: React.FC<VerificationQueueProps> = ({
  programs,
  isLoading,
  error,
  onSelectProgram,
}) => {
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#6b7280' 
      }}>
        Loading verification queue...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '16px',
        borderRadius: '8px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        color: '#dc2626',
      }}>
        <strong>Error loading verification queue:</strong> {error.message}
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div style={{
        padding: '48px',
        textAlign: 'center',
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
      }}>
        <h3 style={{ color: '#374151', marginBottom: '8px' }}>No programs pending verification</h3>
        <p style={{ color: '#6b7280' }}>All extractions have been verified or no data available.</p>
        <p style={{ color: '#6b7280', marginTop: '16px' }}>
          Click "Create Sample Data" above to generate test verification records.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        marginBottom: '16px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
          Verification Queue ({programs.length} programs)
        </h2>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Sorted by priority score (higher = more urgent)
        </div>
      </div>

      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Program
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Fields
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Pending
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Low Confidence
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Avg Confidence
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Priority
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Last Extraction
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: '#374151',
                borderBottom: '1px solid #e5e7eb' 
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {programs.map((program, index) => (
              <tr
                key={program.programId}
                style={{
                  borderBottom: index < programs.length - 1 ? '1px solid #f3f4f6' : 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div>
                    <div style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>
                      {program.programName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      ID: {program.programId.slice(0, 8)}...
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>
                    {program.totalFields}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: program.pendingFields > 0 ? '#fef3c7' : '#d1fae5',
                    color: program.pendingFields > 0 ? '#92400e' : '#065f46',
                  }}>
                    {program.pendingFields}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: program.lowConfidenceFields > 0 ? '#fee2e2' : '#f3f4f6',
                    color: program.lowConfidenceFields > 0 ? '#dc2626' : '#6b7280',
                  }}>
                    {program.lowConfidenceFields}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getConfidenceColor(program.avgConfidence),
                      }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      {program.avgConfidence.toFixed(1)}%
                    </span>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                      ({getConfidenceLabel(program.avgConfidence)})
                    </span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: program.priorityScore >= 100 ? '#fee2e2' : program.priorityScore >= 50 ? '#fef3c7' : '#f0f9ff',
                    color: program.priorityScore >= 100 ? '#dc2626' : program.priorityScore >= 50 ? '#92400e' : '#1e40af',
                  }}>
                    {getPriorityLabel(program.priorityScore)} ({program.priorityScore})
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', color: '#6b7280' }}>
                  {new Date(program.lastExtraction).toLocaleDateString()} {new Date(program.lastExtraction).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <button
                    onClick={() => onSelectProgram(program)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #3b82f6',
                      backgroundColor: '#3b82f6',
                      color: '#ffffff',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2563eb';
                      e.stopPropagation();
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b82f6';
                    }}
                  >
                    Review Fields
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};